/**
 * TicketShipmentActionBlocks.tsx
 *
 * Ticket-level blocks driven by the vendor receipt and inspection workflow on Repair / Replace / Return tickets:
 *   - negligent damage notice, shown from the outset
 *   - "Action required from [Location]" site response to a flagged variance, then its read-only summary
 *   - "RMA declined: replacement purchase order required" banner with PO entry
 * Renders nothing for tickets without shipment data or when the viewer has no access to it.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ActivityIndicator, Platform, Pressable, StyleSheet, Text, TextInput, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useToast } from "../../../components/LhToastContainer";
import {
  DeclinedBanner,
  ShipmentBundle,
  SiteOutcome,
  getShipmentBundle,
  submitReplacementPo,
  submitSiteResponse,
} from "../../../api/shipmentApi";
import { SITE_OUTCOME_LABELS, computeRecordedVariances } from "../util/shipmentReceiptUtils";
import { DOCUMENT_UPLOAD_ERROR, pickDocument, uploadPickedDocument } from "../util/pickDocument";
import { formatDateWithTimezone } from "../util/dateFormatter";
import { sanitizeFreeText } from "../util/sanitizeFreeText";

export interface TicketShipmentActionBlocksProps {
  /** Numeric backend ticket ID. */
  ticketId: number;
  /** Bump to reload after a change made elsewhere on the ticket (for example in the Shipping and Vendor Receipt dialog). */
  refreshKey?: number;
  /** Called after a change so the parent can refetch the ticket (status and activity stream). */
  onMutated?: () => void;
}

export const SITE_RESPONSE_REQUIRED = "Record what the site found before responding.";
export const PO_REQUIRED = "Enter the replacement purchase order number.";
const SITE_RESPONSE_MAX = 2000;

const MONO = Platform.OS === "ios" ? "Courier" : "monospace";

// ---------------------------------------------------------------------------
// Declined unit banner with PO entry
// ---------------------------------------------------------------------------

const DeclinedUnitBanner: React.FC<{
  ticketId: number;
  banner: DeclinedBanner;
  onRecorded: (message: string) => void;
}> = ({ ticketId, banner, onRecorded }) => {
  const [po, setPo] = useState("");
  const [attachment, setAttachment] = useState<{ name: string; blobName: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const attach = async () => {
    if (uploading) return;
    setError(null);
    setUploading(true);
    try {
      const picked = await pickDocument();
      if (!picked) return;
      setAttachment({ name: picked.name, blobName: await uploadPickedDocument(picked) });
    } catch (err: any) {
      setError(err?.message || DOCUMENT_UPLOAD_ERROR);
    } finally {
      setUploading(false);
    }
  };

  const record = async () => {
    if (busy) return;
    const poNumber = po.trim();
    if (!poNumber) { setError(PO_REQUIRED); return; }
    setError(null);
    setBusy(true);
    const r = await submitReplacementPo(ticketId, {
      serial: banner.serialNumber,
      poNumber,
      attachmentBlobName: attachment?.blobName ?? null,
    });
    setBusy(false);
    if (r.error || !r.data) { setError(r.error ?? "Failed to record the replacement PO."); return; }
    onRecorded(r.data.message);
  };

  const recorded = !!banner.poRecorded;

  return (
    <View style={[s.declined, recorded && s.declinedDone]}>
      <View style={s.bannerHead}>
        <MaterialIcons name={recorded ? "check-circle" : "warning"} size={18} color={recorded ? "#027A48" : "#B42318"} />
        <Text style={[s.declinedTitle, recorded && { color: "#027A48" }]}>
          {recorded ? "Replacement purchase order recorded" : (banner.title ?? "RMA declined: replacement purchase order required")}
        </Text>
      </View>
      {banner.message ? <Text style={s.declinedBody}>{banner.message}</Text> : null}

      <View style={s.declinedDetail}>
        <Text style={s.declinedSerial}>{banner.serialNumber}</Text>
        <Text style={s.declinedCat}>{(banner.failureCategories ?? []).join(", ")}</Text>
        {banner.inspectorNotes ? <Text style={s.declinedNotes}>{banner.inspectorNotes}</Text> : null}
      </View>

      {recorded ? (
        <Text style={s.recordedPo}>PO number: {banner.replacementPoNumber ?? "-"}</Text>
      ) : banner.canEnterPo ? (
        <View style={s.poRow}>
          <View style={s.poField}>
            <Text style={s.label}>PO NUMBER <Text style={s.req}>*</Text></Text>
            <TextInput
              style={[s.input, !!error && error === PO_REQUIRED && s.inputError]}
              value={po}
              onChangeText={(v) => { setPo(v.replace(/[^A-Za-z0-9\-_/]/g, "").slice(0, 40)); setError(null); }}
              placeholder="e.g. 4500128877"
              placeholderTextColor="#9ca3af"
              autoCapitalize="characters"
              autoCorrect={false}
              editable={!busy}
              accessibilityLabel="Replacement PO number"
            />
          </View>
          <Pressable style={({ pressed }) => [s.btnOutline, pressed && s.pressed]} onPress={attach} disabled={uploading || busy}>
            {uploading ? <ActivityIndicator size="small" color="#034895" /> : <Text style={s.btnOutlineText}>Attach PO</Text>}
          </Pressable>
          <Pressable style={({ pressed }) => [s.btnPrimary, pressed && s.pressed, (busy || uploading) && s.disabled]} onPress={record} disabled={busy || uploading}>
            {busy ? <ActivityIndicator size="small" color="#ffffff" /> : <Text style={s.btnPrimaryText}>Record PO</Text>}
          </Pressable>
        </View>
      ) : (
        <Text style={s.hint}>The submitter or a Secondary Stakeholder records the replacement PO number.</Text>
      )}
      {attachment && !recorded ? (
        <View style={s.fileChip}>
          <Text style={s.fileChipText} numberOfLines={1}>{attachment.name}</Text>
          <Pressable onPress={() => setAttachment(null)} hitSlop={8}><MaterialIcons name="close" size={14} color="#6b7280" /></Pressable>
        </View>
      ) : null}
      {error ? <Text style={s.errorText}>{error}</Text> : null}
    </View>
  );
};

// ---------------------------------------------------------------------------
// Site response block
// ---------------------------------------------------------------------------

const SiteResponseBlock: React.FC<{
  ticketId: number;
  bundle: ShipmentBundle;
  onRecorded: (message: string) => void;
}> = ({ ticketId, bundle, onRecorded }) => {
  const d = bundle.receipt?.discrepancy;
  const [text, setText] = useState("");
  const [busy, setBusy] = useState<SiteOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);

  const variances = useMemo(
    () => (bundle.receipt?.skus && !d?.flaggedOnly ? computeRecordedVariances(bundle.receipt.skus, d?.unexpectedSerials) : []),
    [bundle.receipt?.skus, d?.flaggedOnly, d?.unexpectedSerials]
  );

  if (!d) return null;

  const respond = async (outcome: SiteOutcome) => {
    if (busy) return;
    const clean = sanitizeFreeText(text, SITE_RESPONSE_MAX);
    if (!clean) { setError(SITE_RESPONSE_REQUIRED); return; }
    setError(null);
    setBusy(outcome);
    const r = await submitSiteResponse(ticketId, { outcome, text: clean });
    setBusy(null);
    if (r.error || !r.data) { setError(r.error ?? "Failed to record the site response."); return; }
    onRecorded(r.data.message);
  };

  if (d.siteResponse) {
    return (
      <View style={s.responseDone}>
        <View style={s.bannerHead}>
          <MaterialIcons name="check-circle" size={18} color="#027A48" />
          <Text style={[s.declinedTitle, { color: "#027A48" }]}>Site response recorded</Text>
        </View>
        <Text style={s.responseOutcome}>{SITE_OUTCOME_LABELS[d.siteResponse.outcome] ?? d.siteResponse.outcome}</Text>
        <Text style={s.bodyText}>{d.siteResponse.text}</Text>
        <Text style={s.hint}>{d.siteResponse.respondedBy ?? "Site"} · {formatDateWithTimezone(d.siteResponse.respondedAt)}</Text>
      </View>
    );
  }

  if (!d.canRespond) return null;

  return (
    <View style={s.action}>
      <View style={s.bannerHead}>
        <MaterialIcons name="assignment-late" size={18} color="#B54708" />
        <Text style={s.actionTitle}>{d.actionTitle ?? "Action required from the site"}</Text>
      </View>
      <Text style={s.bodyText}>
        The vendor reported a difference between what was shipped and what arrived.
      </Text>
      {variances.length > 0 ? (
        <View style={{ marginTop: 6 }}>
          {variances.map((v) => (
            <Text key={v.key} style={s.varianceLine}>
              {v.sku ? <Text style={s.mono}>{v.sku}: </Text> : null}
              {v.detail}
            </Text>
          ))}
        </View>
      ) : null}
      {d.notes ? <View style={s.notesBox}><Text style={s.bodyText}>{d.notes}</Text></View> : null}
      <Text style={[s.label, { marginTop: 10 }]}>STEPS</Text>
      <Text style={s.step}>1. Recheck the staging area, the dock, and any secondary storage for the units reported missing.</Text>
      <Text style={s.step}>2. Confirm the count physically packed against the retained packing slip copy.</Text>
      <Text style={s.step}>3. Record the outcome below.</Text>
      <Text style={s.statement}>Warehouse Support cannot reconcile the shipment until the site responds.</Text>

      <Text style={[s.label, { marginTop: 10 }]}>WHAT DID THE SITE FIND? <Text style={s.req}>*</Text></Text>
      <TextInput
        style={[s.notesInput, error === SITE_RESPONSE_REQUIRED && s.inputError]}
        value={text}
        onChangeText={(v) => { setText(v.slice(0, SITE_RESPONSE_MAX)); setError(null); }}
        multiline
        numberOfLines={3}
        maxLength={SITE_RESPONSE_MAX}
        editable={!busy}
        accessibilityLabel="Site response"
      />
      {error ? <Text style={s.errorText}>{error}</Text> : null}
      <View style={s.actions}>
        <Pressable style={({ pressed }) => [s.btnOutline, pressed && s.pressed, !!busy && s.disabled]} onPress={() => respond("UNITS_CANNOT_BE_LOCATED")} disabled={!!busy}>
          {busy === "UNITS_CANNOT_BE_LOCATED" ? <ActivityIndicator size="small" color="#034895" /> : <Text style={s.btnOutlineText}>{SITE_OUTCOME_LABELS.UNITS_CANNOT_BE_LOCATED}</Text>}
        </Pressable>
        <Pressable style={({ pressed }) => [s.btnPrimary, pressed && s.pressed, !!busy && s.disabled]} onPress={() => respond("UNITS_LOCATED_RESHIPPED")} disabled={!!busy}>
          {busy === "UNITS_LOCATED_RESHIPPED" ? <ActivityIndicator size="small" color="#ffffff" /> : <Text style={s.btnPrimaryText}>{SITE_OUTCOME_LABELS.UNITS_LOCATED_RESHIPPED}</Text>}
        </Pressable>
      </View>
    </View>
  );
};

// ---------------------------------------------------------------------------
// Container
// ---------------------------------------------------------------------------

const TicketShipmentActionBlocks: React.FC<TicketShipmentActionBlocksProps> = ({ ticketId, refreshKey = 0, onMutated }) => {
  const toast = useToast();
  const [bundle, setBundle] = useState<ShipmentBundle | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await getShipmentBundle(ticketId);
    // Hidden when the viewer has no access or the call fails; the ticket page must not break.
    if (r.forbidden) { setBundle(null); return; }
    if (r.data) setBundle(r.data);
  }, [ticketId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleDone = (message: string) => {
    setNotice(message);
    toast.show(message, "success");
    load();
    onMutated?.();
  };

  if (!bundle) return null;

  const insp = bundle.inspections;
  const banners = (insp?.declinedBanners ?? []).filter((b) => !!b.serialNumber);
  const awaitingPo = banners.some((b) => !b.poRecorded);
  const showNegligentNotice = !!insp?.negligentDamageNotice && !insp?.hasDeclinedUnit && !!insp?.negligentDamageMessage;
  const showSiteBlock = !!bundle.receipt?.discrepancy && (!!bundle.receipt.discrepancy.siteResponse || !!bundle.receipt.discrepancy.canRespond);

  if (!notice && !showNegligentNotice && banners.length === 0 && !showSiteBlock && !insp?.hasDeclinedUnit) return null;

  return (
    <View style={s.wrap}>
      {notice ? (
        <View style={s.notice}>
          <MaterialIcons name="check-circle" size={16} color="#027A48" />
          <Text style={s.noticeText}>{notice}</Text>
          <Pressable onPress={() => setNotice(null)} hitSlop={8}><MaterialIcons name="close" size={14} color="#027A48" /></Pressable>
        </View>
      ) : null}

      {insp?.hasDeclinedUnit ? (
        <View style={s.chip}><Text style={s.chipText}>Declined, negligent damage</Text></View>
      ) : null}

      {showNegligentNotice ? (
        <View style={s.negligent}>
          <MaterialIcons name="info-outline" size={18} color="#B54708" />
          <Text style={s.negligentText}>{insp?.negligentDamageMessage}</Text>
        </View>
      ) : null}

      {showSiteBlock ? <SiteResponseBlock ticketId={ticketId} bundle={bundle} onRecorded={handleDone} /> : null}

      {awaitingPo ? (
        <View style={s.waiting}>
          <Text style={s.waitingLabel}>WAITING ON</Text>
          <Text style={s.waitingText}>
            <Text style={{ fontWeight: "700" }}>Site contact and Site Leadership</Text> to raise a replacement PO. They have been notified by email and in app.
          </Text>
        </View>
      ) : null}
      {banners.map((b) => (
        <DeclinedUnitBanner key={b.serialNumber} ticketId={ticketId} banner={b} onRecorded={handleDone} />
      ))}
    </View>
  );
};

const s = StyleSheet.create({
  wrap: { marginBottom: 12, gap: 10 },
  notice: { flexDirection: "row", alignItems: "center", gap: 8, backgroundColor: "#ECFDF3", borderWidth: 1, borderColor: "#ABEFC6", borderRadius: 8, padding: 10 },
  noticeText: { flex: 1, fontSize: 12, fontWeight: "600", color: "#027A48" },
  chip: { alignSelf: "flex-start", backgroundColor: "#FEE4E2", borderRadius: 100, paddingHorizontal: 10, paddingVertical: 4 },
  chipText: { fontSize: 11, fontWeight: "700", color: "#B42318" },
  negligent: { flexDirection: "row", gap: 10, alignItems: "flex-start", backgroundColor: "#FFFAEB", borderWidth: 1, borderColor: "#FEC84B", borderRadius: 8, padding: 12 },
  negligentText: { flex: 1, fontSize: 13, color: "#7A2E0E", lineHeight: 19 },
  waiting: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 8, backgroundColor: "#EEF4FB", borderRadius: 8, paddingHorizontal: 14, paddingVertical: 10 },
  waitingLabel: { fontSize: 10, fontWeight: "700", color: "#034895", letterSpacing: 0.6 },
  waitingText: { flex: 1, fontSize: 13, color: "#1f2937", lineHeight: 19, minWidth: 200 },
  declined: { backgroundColor: "#FEF3F2", borderWidth: 1, borderColor: "#FDA29B", borderLeftWidth: 4, borderLeftColor: "#B42318", borderRadius: 8, padding: 14 },
  declinedDone: { backgroundColor: "#F0FDF4", borderColor: "#BBF7D0", borderLeftColor: "#16a34a" },
  bannerHead: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 6 },
  declinedTitle: { fontSize: 14, fontWeight: "700", color: "#B42318", flex: 1 },
  declinedBody: { fontSize: 13, color: "#475467", lineHeight: 19 },
  declinedDetail: { flexDirection: "row", flexWrap: "wrap", alignItems: "flex-start", gap: 14, marginTop: 10, paddingVertical: 8 },
  declinedSerial: { fontSize: 12, fontWeight: "700", color: "#111827", fontFamily: MONO },
  declinedCat: { fontSize: 12, color: "#111827", flexShrink: 1 },
  declinedNotes: { flexBasis: 260, flexGrow: 1, fontSize: 11, color: "#6b7280", lineHeight: 16 },
  recordedPo: { fontSize: 13, fontWeight: "700", color: "#027A48", marginTop: 6 },
  poRow: { flexDirection: "row", flexWrap: "wrap", alignItems: "flex-end", gap: 10, marginTop: 8 },
  poField: { minWidth: 200 },
  label: { fontSize: 10, fontWeight: "700", color: "#6b7280", letterSpacing: 0.5, marginBottom: 6 },
  req: { color: "#B42318" },
  input: { height: 36, borderWidth: 1, borderColor: "#D0D0D0", borderRadius: 6, paddingHorizontal: 10, fontSize: 13, color: "#2D2D2D", backgroundColor: "#ffffff" },
  inputError: { borderColor: "#B42318", borderWidth: 1.5 },
  fileChip: { flexDirection: "row", alignItems: "center", alignSelf: "flex-start", gap: 6, maxWidth: 280, marginTop: 8, backgroundColor: "#f3f4f6", borderRadius: 100, paddingHorizontal: 10, paddingVertical: 5 },
  fileChipText: { fontSize: 11, color: "#374151", flexShrink: 1 },
  errorText: { fontSize: 12, color: "#B42318", marginTop: 6, lineHeight: 17 },
  hint: { fontSize: 11, color: "#6b7280", marginTop: 6, lineHeight: 16 },
  action: { backgroundColor: "#FFFAEB", borderWidth: 1, borderColor: "#FEC84B", borderLeftWidth: 4, borderLeftColor: "#F79009", borderRadius: 8, padding: 14 },
  actionTitle: { fontSize: 14, fontWeight: "700", color: "#7A2E0E", flex: 1 },
  bodyText: { fontSize: 13, color: "#374151", lineHeight: 19 },
  varianceLine: { fontSize: 12, color: "#7A2E0E", lineHeight: 18 },
  mono: { fontWeight: "700", fontFamily: MONO },
  notesBox: { marginTop: 8, backgroundColor: "#ffffff", borderWidth: 1, borderColor: "#e5e7eb", borderRadius: 6, padding: 10 },
  step: { fontSize: 13, color: "#374151", lineHeight: 20 },
  statement: { fontSize: 12, fontWeight: "700", color: "#7A2E0E", marginTop: 8 },
  notesInput: { minHeight: 72, borderWidth: 1, borderColor: "#D0D0D0", borderRadius: 6, padding: 10, fontSize: 13, color: "#2D2D2D", backgroundColor: "#ffffff", textAlignVertical: "top" },
  actions: { flexDirection: "row", justifyContent: "flex-end", flexWrap: "wrap", gap: 10, marginTop: 12 },
  responseDone: { backgroundColor: "#F0FDF4", borderWidth: 1, borderColor: "#BBF7D0", borderLeftWidth: 4, borderLeftColor: "#16a34a", borderRadius: 8, padding: 14 },
  responseOutcome: { fontSize: 13, fontWeight: "700", color: "#166534", marginBottom: 4 },
  btnOutline: { minHeight: 38, borderWidth: 1.5, borderColor: "#034895", borderRadius: 100, paddingHorizontal: 16, alignItems: "center", justifyContent: "center", backgroundColor: "#ffffff" },
  btnOutlineText: { fontSize: 13, fontWeight: "700", color: "#034895" },
  btnPrimary: { minHeight: 38, borderRadius: 100, paddingHorizontal: 18, alignItems: "center", justifyContent: "center", backgroundColor: "#034895" },
  btnPrimaryText: { fontSize: 13, fontWeight: "700", color: "#ffffff" },
  pressed: { opacity: 0.8 },
  disabled: { opacity: 0.55 },
});

export default TicketShipmentActionBlocks;
